import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { AccommodationMonthView } from './AccommodationCalendarMonthPicker'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Heading from 'LuxKit/Typography/Heading'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'

const MonthCell = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};
  min-height: ${rem(70)};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.sold-out {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

const CellTitle = styled(Group)`
  min-height: ${rem(24)};
`

interface Props {
  view: AccommodationMonthView
  onSelect?: (view: AccommodationMonthView) => void
  cheapest?: boolean
  priceType?: 'default' | 'member'
}

function AccommodationCalendarMonthCell(props: Props) {
  const { view, onSelect, cheapest, priceType } = props

  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const date = useMemo(() => new Date(view.year, view.month, 1), [view.month, view.year])

  const onMonthCellClick = useCallback(() => onSelect?.(view), [onSelect, view])

  let priceColour: React.ComponentProps<typeof BodyText>['colour']
  if (cheapest) {
    priceColour = 'highlight-secondary'
  } else if (priceType === 'member') {
    priceColour = 'lux-plus'
  }

  const soldOutText = intl.formatMessage({
    id: 'P+FJZCpvfc',
    defaultMessage: 'SOLD OUT',
    description: 'Label shown for a month option when all dates are sold out.',
  })

  const pricingLabel = useMemo(() => {
    if (!view.pricing) return undefined

    if (!view.duration) {
      return intl.formatMessage({
        id: 'wYWcpjLCPZ',
        defaultMessage: 'from',
        description: 'Label shown before the starting price for a month option.',
      })
    }

    if (view.durationUnit === 'day') {
      return intl.formatMessage(
        {
          id: 'gQC1SMj59G',
          defaultMessage: '{count, plural, one {# day} other {# days}} from',
          description: 'Duration label in days with "from" shown above month pricing.',
        },
        { count: view.duration },
      )
    }

    if (view.durationUnit === 'night') {
      return intl.formatMessage(
        {
          id: 'P1NfJBtj9N',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Duration label in nights with "from" shown above month pricing.',
        },
        { count: view.duration },
      )
    }

    return undefined
  }, [view, intl])

  return <MonthCell
    disabled={view.soldOut && !view.soldOutExtraActionText}
    className={clsx({ 'sold-out': view.soldOut })}
    onClick={onMonthCellClick}
  >
    <CellTitle direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
      <Group direction="horizontal" gap={4}>
        <Heading variant="heading6">
          {dateFormatter(date, INTL_SHORT_MONTH)}
        </Heading>
        {view.featured && <SolidFireIcon colour="urgency" size="XS" />}
      </Group>
      {view.label}
    </CellTitle>
    <VerticalSpacer gap={2}>
      {!view.pricing && view.soldOut && <VerticalSpacer gap={2}>
        <Caption variant="large" colour="neutral-four">{soldOutText}</Caption>
        {!!view.soldOutExtraActionText && <TextLink size="caption" weight="regular">
          {view.soldOutExtraActionText}
        </TextLink>}
      </VerticalSpacer>}
      {!!view.pricing && <>
        {pricingLabel && <Caption variant="large" colour="neutral-three">
          {pricingLabel}
        </Caption>}
        <div>
          <BodyText variant="medium" weight="bold" as="span" colour={priceColour}>
            <FormatCurrency value={view.pricing.price} format="roundedDollar" />
          </BodyText>{' '}
          <Caption variant="large" colour="neutral-three" as="span">
            {view.pricing.saleUnit}
          </Caption>
        </div>
      </>}
    </VerticalSpacer>
  </MonthCell>
}

export default AccommodationCalendarMonthCell
